import React from "react";
import { useRecoilValue, useSetRecoilState, useRecoilState } from "recoil";

import {
	layerDefVisibilitySelector,
	layerDefSourceDataSelector,
} from "../../recoil";

import {
	haveCreds,
	haveAllOptions,
	fetchTravelTimeCredentials,
	fetchTravelTimeData,
} from './api'

import {
	newTravelTimeDataCacheEntry,
	travelTimeLoadingAtom,
	travelTimeRenderingAtom,
	travelTimeDataCacheAtom,
	travelTimeOptionsAtom,
	travelTimeOptionsSelector
} from './atoms'

import { gis2TokenAtom } from '../../../login/loginAtoms.js'

import TravelTimeSlider from './TravelTimeSlider'
import TravelTypeSelector from './TravelTypeSelector'
import TravelTimeRefocusButton from './TravelTimeRefocusButton'

const objectsEqual = (a, b) => {
	const exists = (v) => v !== undefined && v !== null
	if (!exists(a) || !exists(b)) {
		return false
	}

	const aSet = Object.entries(a).sort()
	const bSet = Object.entries(b).sort()

	if (aSet.length !== bSet.length) {
		return false
	}

	for (let i = 0; i < aSet.length; i++) {
		if (aSet[i][0] !== bSet[i][0] || aSet[i][1] !== bSet[i][1]) {
			return false
		}
	}

	return true
}

export default function TravelTimeControls() {
	const setTravelTimeData = useSetRecoilState(layerDefSourceDataSelector('travel-time'))
	const setTravelTimeLoading = useSetRecoilState(travelTimeLoadingAtom)
	const setTravelTimeRendering = useSetRecoilState(travelTimeRenderingAtom)
	const [travelTimeDataCache, setTravelTimeDataCache] = useRecoilState(travelTimeDataCacheAtom)
	const travelTimeLayerVisibility = useRecoilValue(layerDefVisibilitySelector('travel-time'))
	const derivedTravelTimeOptions = useRecoilValue(travelTimeOptionsSelector)
	const [travelTimeOptions, setTravelTimeOptions] = useRecoilState(travelTimeOptionsAtom)
	const gis2Token = useRecoilValue(gis2TokenAtom)
	const [fetchCredsCounter, setFetchCredsCounter] = React.useState(0)

	const noData = {
		type: "FeatureCollection",
		features: [],
	}

	const defaultCreds = { 
		app_id: null,
		api_key: null,
	}

	const setCreds = (creds) => {
		setTravelTimeOptions({
			...travelTimeOptions,
			...creds,
		})
	}

	React.useEffect(() => {
		if (fetchCredsCounter !== 1) {
			return
		}

		if (!gis2Token) {
			setCreds(defaultCreds)
			return		
		}

		if (haveCreds(travelTimeOptions)) {
			return
		}

		fetchTravelTimeCredentials(gis2Token)
			.then((creds) => {
				setCreds(creds)
				setFetchCredsCounter(0)
			})
	}, [fetchCredsCounter])

	React.useEffect(() => {
		if (!travelTimeLayerVisibility) {
			setTravelTimeData(noData)
			return
		}

		if (!haveCreds(derivedTravelTimeOptions)) {
			setFetchCredsCounter(fetchCredsCounter+1)
			return
		}

		if (!haveAllOptions(derivedTravelTimeOptions)) {
			return
		}

		if (objectsEqual(travelTimeDataCache.options, derivedTravelTimeOptions)) {
			setTravelTimeData(travelTimeDataCache.data[0])
			return
		}

		let cancelDataUpdate = false
		setTravelTimeLoading(true)
		setTravelTimeRendering(true)
		const options = structuredClone(derivedTravelTimeOptions)
		
		fetchTravelTimeData(options)
			.then((data) => {
				if (cancelDataUpdate) {
					return
				}

				if (!data) {
					setTravelTimeData(noData)
					return
				}

				setTravelTimeData(data[0])
				setTravelTimeDataCache(
					newTravelTimeDataCacheEntry(options, data),
				)
			})
			.catch((e) => {
				console.error(e)
				setTravelTimeData(noData)
			})
			.finally(() => {
				setTravelTimeLoading(false)
			})

		return () => {
			cancelDataUpdate = true
		}
	}, [derivedTravelTimeOptions, travelTimeLayerVisibility])

	return (
		<>
			<TravelTimeRefocusButton />
			<TravelTypeSelector />
			<TravelTimeSlider  />
		</>
	)
}